import React, { useEffect, useState, useRef } from 'react';
import SidebarAdUpload from './Sidebars/SidebarAdUpload'; 
import { useLocation, useNavigate, Link } from 'react-router-dom';
import api from '../../shared/api/axios';
import { useAuth } from '../../shared/contexts/AuthContext';
import { useMediaPreview } from '../../shared/contexts/MediaPreviewContext';
import { ensureJpeg } from '../../shared/utils/ensureJpeg';
import { ensureMp4H264Aac } from '../../shared/utils/videoPipeline';
import { getOverlayForFilter, type FilterPresetId } from '../../shared/components/filterHelpers.web';
import { useTranslation } from 'react-i18next';
import ChoshiiLogo from '../ChoshiiLogo';

import type { MediaItem } from '../../shared/contexts/MediaPreviewContext';
type ExtMediaItem = MediaItem & { musicId?: string | null };



// Accepts string or object from server and preserves music meta
// 1) helper to pull a usable absolute url from server item
const extractServerUrl = (m: any): string => {
  const cand =
    m?.url ?? m?.videoUrl ?? m?.src ?? m?.path ?? m?.file ??
    m?.media?.url ?? m?.video?.url ?? m?.image?.url ??
    m?.url?.secure_url ?? m?.url?.url ?? "";

  const s = String(cand || "").trim();
  if (!s || s.startsWith("blob:")) {
    // If your API returns a key, reconstruct here:
    // if (m.key) return new URL(`/uploads/${m.key}`, getAssetsBase()).toString();
    return ""; // unusable
  }
  return s;
};

const fromServerMedia = (m: any, index: number): ExtMediaItem => {
  const url = extractServerUrl(m);
  const filterId = m?.filterId ?? m?.filter?.id ?? m?.filter?.name ?? null;
  const musicId =
   m?.musicId ?? m?.music?._id ?? m?.audio?._id ??
   m?.trackId ?? null;

  return {
    id: `existing-${index}`,
    url,
    musicId,
    musicTitle:
      m?.musicTitle ?? m?.songTitle ?? m?.trackTitle ??
      m?.music?.title ?? m?.audio?.title ?? null,
    musicArtist:
      m?.musicArtist ?? m?.songArtist ?? m?.trackArtist ??
      m?.music?.artist ?? m?.audio?.artist ?? null,
    filterId,
  };
};

// read a media item's filter from context (or null)
const useFilterLookup = () => {
  const { mediaItems } = useMediaPreview();
  return (id: string) => mediaItems.find(m => m.id === id)?.filterId ?? null;
};

// build overlay style for a given filterId
const overlayStyleFor = (fid?: string | null): React.CSSProperties | null => {
  const id = (fid as FilterPresetId) || 'none';
  const { tint, opacity } = getOverlayForFilter(id);
  return tint ? { position: 'absolute', inset: 0, backgroundColor: tint, opacity, pointerEvents: 'none', borderRadius: 8 } : null;
};

const MAX_POST_MEDIA_BYTES = Math.round(1.7 * 1024 * 1024 * 1024); // ~1.7 GB
const MAX_MEDIA_PER_POST = 8;

// 3 minutes + 3 seconds (same as mobile HARD_CAP_SEC = 183)
const HARD_CAP_VIDEO_SEC = 183;
const MAX_VIDEO_DURATION_MS = HARD_CAP_VIDEO_SEC * 1000;

const AdUpload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search || ''}`;
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const [targetUrl, setTargetUrl] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');


  const [isDragOver, setIsDragOver] = useState(false);


  const [isTranscoding, setIsTranscoding] = useState(false);
  const { mediaItems, setMediaItems, addOrReplaceMediaItem } = useMediaPreview();
  const navState = location.state || {};
  const { editTargetId, previewFilename, isPreview, previewFile, draft } = navState;
  const hasInjectedRef = useRef(false);

  const shouldReset = navState?.reset === true;
  const hasHydratedDraft = useRef(false);
  const hasReset = useRef(false);

const getFilterId = useFilterLookup();

const getApiBaseUrl = () =>
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001').replace(/\/api\/?$/, '');

const toRemoteHttp = (u?: string) => {
  if (!u) return undefined;
  if (/^https?:\/\//i.test(u)) return u;                 // already http(s)
  if (u.startsWith('/')) return `${getApiBaseUrl()}${u}`;
  return undefined;                                       // <- DO NOT pass blob: or local objectURLs
};

async function prepareFileForUpload(file: File): Promise<File> {
  if (file.type.startsWith('image/')) {
    return await ensureJpeg(file, 0.85);
  }

  if (file.type.startsWith('video/')) {
    try {
      const out = await ensureMp4H264Aac(file); // will await load() internally
      // normalize extension if needed
      if (out.type === 'video/mp4' && !/\.mp4$/i.test(out.name)) {
        const base = out.name.replace(/\.[^.]+$/, '');
        return new File([out], `${base}.mp4`, { type: 'video/mp4' });
      }
      return out;
    } catch (e) {
      console.warn('ffmpeg wasm unavailable, skipping client transcode:', e);
      // Soft fallback — just give it a .mp4-ish name; backend will transcode if needed
      const name = /\.mp4$/i.test(file.name)
        ? file.name
        : file.name.replace(/\.[^.]+$/, '') + '.mp4';
      return new File([file], name, { type: file.type || 'video/mp4' });
    }
  }

  return file;
}


useEffect(() => {
  if (!isPreview || !previewFilename || !previewFile) return;

  const previewId = editTargetId || `preview-${Date.now()}`;
  if (hasInjectedRef.current === previewFilename) return;

  const objectURL = URL.createObjectURL(previewFile);

  const existing = mediaItems.find(m => m.id === previewId);

  addOrReplaceMediaItem({
    id: previewId,
    file: previewFile,
    url: existing?.url,
    musicTitle: existing?.musicTitle ?? null,
    musicArtist: existing?.musicArtist ?? null,
    filterId: existing?.filterId ?? null, 
  });

  setObjectURLs((prev) => ({
    ...prev,
    [previewId]: objectURL,
  }));

  hasInjectedRef.current = previewFilename;

// ⛔️ no cleanup that revokes objectURL here
}, [isPreview, previewFilename, previewFile, editTargetId, mediaItems, addOrReplaceMediaItem]);

  // 1️⃣  place this helper above your component (or inside it)
const isVideo = (item: MediaItem): boolean => {
  if (item.file) return item.file.type.startsWith('video/');
  if (item.url)  return /\.(mp4|webm|mov|m4v)$/i.test(item.url);
  return false;
};

console.log("Received media items:", mediaItems);
  const { postId } = location.state || {};
  const [existingMedia, setExistingMedia] = useState<ExtMediaItem[]>([]);
  const [objectURLs, setObjectURLs] = useState<Record<string, string>>({});

  const totalMediaCount = existingMedia.length + mediaItems.length;

  
  // Read video duration from a File (in ms), or null if unknown
const getVideoDurationMsFromFile = (file: File): Promise<number | null> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('video/')) {
      resolve(null);
      return;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const dur = video.duration; // seconds
      if (!isFinite(dur) || dur <= 0) {
        resolve(null);
      } else {
        resolve(Math.round(dur * 1000)); // ms
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    video.src = url;
  });
};

//Saving draft
useEffect(() => {
  if (shouldReset) return;                // reset wins
  if (!draft) return;
  if (hasHydratedDraft.current) return;
  hasHydratedDraft.current = true;

  // restore sidebar fields
  if (typeof draft.targetUrl === 'string') setTargetUrl(draft.targetUrl);
  if (typeof draft.buttonLabel === 'string') setButtonLabel(draft.buttonLabel);

  // restore media
  if (draft.mediaDraft?.mediaItems) setMediaItems(draft.mediaDraft.mediaItems);
  if (draft.mediaDraft?.existingMedia) setExistingMedia(draft.mediaDraft.existingMedia);
}, [draft, shouldReset, setMediaItems]);


const [activeVideoChipId, setActiveVideoChipId] = useState<string | null>(null);
const chipHideTimeoutRef = useRef<number | null>(null);

const showMusicChipFor = (id: string) => {
  setActiveVideoChipId(id);

  if (chipHideTimeoutRef.current) {
    window.clearTimeout(chipHideTimeoutRef.current);
  }

  chipHideTimeoutRef.current = window.setTimeout(() => {
    setActiveVideoChipId(prev => (prev === id ? null : prev));
    chipHideTimeoutRef.current = null;
  }, 2500); // ≈ when native controls fade
};

useEffect(() => {
  return () => {
    if (chipHideTimeoutRef.current) {
      window.clearTimeout(chipHideTimeoutRef.current);
    }
  };
}, []);


  const mediaLabel = (m?: Pick<MediaItem, 'musicTitle' | 'musicArtist'>) =>
    [m?.musicTitle, m?.musicArtist].filter(Boolean).join(' — ');
  
  //Fetch existing media data if postId is provided
  useEffect(() => {
    const fetchMediaData = async () => {
      if (!postId) return;
  
      try {
        const res = await api.get(`/events/${postId}`);
        // server may return strings or objects
        const rawList: any[] = Array.isArray(res.data?.media) ? res.data.media : [];
  
        const fetchedMedia: ExtMediaItem[] = rawList.map(fromServerMedia);
        setExistingMedia(fetchedMedia);
      } catch (err) {
        console.error("Failed to fetch media data:", err);
      }
    };
  
    fetchMediaData();
  }, [postId]);


 //Handle file selection
// Put this near the top-level of the component (once)
const uid = () =>
  (globalThis.crypto?.randomUUID?.() ??
    `id-${Date.now()}-${Math.random().toString(36).slice(2)}`);

// ⬇️ new shared helper
const processPickedFiles = async (picked: File[]) => {
  if (picked.length === 0) return;

  const existingCount = existingMedia.length + mediaItems.length;
  let remainingSlots = Math.max(0, MAX_MEDIA_PER_POST - existingCount);

  if (remainingSlots <= 0) {
    alert(t('ad.alerts.mediaPerPostLimit'));
    return;
  }

  setIsTranscoding(true);

  const newItems: MediaItem[] = [];
  const newUrls: Record<string, string> = {};

  const existingNames = new Set(
    mediaItems.map(m => m.file?.name).filter(Boolean) as string[]
  );

  let skippedByDuration = 0;
  let skippedByCap = 0;

  for (const file of picked) {
    if (remainingSlots <= 0) {
      skippedByCap++;
      continue;
    }

    // skip duplicates by filename
    if (existingNames.has(file.name)) continue;

    // 🎬 duration check for videos
    if (file.type.startsWith('video/')) {
      const durMs = await getVideoDurationMsFromFile(file);
      if (durMs != null && durMs > MAX_VIDEO_DURATION_MS) {
        skippedByDuration++;
        continue;
      }
    }

    const id = `new-${uid()}`;
    newItems.push({ id, file } as MediaItem);
    newUrls[id] = URL.createObjectURL(file);
    remainingSlots--;
  }

  if (newItems.length === 0) {
    setIsTranscoding(false);

    if (skippedByDuration > 0) {
      alert(t('ad.alerts.videosSkippedTooLongShort'));
    }
    if (skippedByCap > 0) {
      alert(t('ad.alerts.mediaPerPostHardLimit'));
    }

    return;
  }

  setMediaItems(prev => [...prev, ...newItems]);
  setObjectURLs(prev => ({ ...prev, ...newUrls }));
  setIsTranscoding(false);

  if (skippedByDuration > 0) {
    alert(t('ad.alerts.videosSkippedTooLongSummary'));
  }
  if (skippedByCap > 0) {
    alert(t('ad.alerts.filesSkippedByCapSummary'));
  }
};

// ⬇️ existing input handler now just delegates
const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const picked = Array.from(e.target.files || []);
  await processPickedFiles(picked);
};

// ⬇️ NEW: drop handler (used by the drop zone)
const handleDropFiles = async (e: React.DragEvent<HTMLDivElement>) => {
  e.preventDefault();
  e.stopPropagation();
  setIsDragOver(false);

  if (isTranscoding) return;

  const picked = Array.from(e.dataTransfer.files || []);
  await processPickedFiles(picked);
};



  const handleDelete = (id: string) => {
    const updated = mediaItems.filter(item => item.id !== id);
    setMediaItems(updated);
  
    if (objectURLs[id]) {
      URL.revokeObjectURL(objectURLs[id]);
      setObjectURLs((prev) => {
        const updatedURLs = { ...prev };
        delete updatedURLs[id];
        return updatedURLs;
      });
    }
    };

    const handleOpenMusic = async (entry: {
      musicId?: string | null;
      musicTitle?: string | null;
      musicArtist?: string | null;
    }) => {
      // 1️⃣ Best case: we already know the track id
      if (entry.musicId) {
        navigate(`/musicdetails/${entry.musicId}`);
        return;
      }
    
      // 2️⃣ Fallback: search by EITHER title or artist (not "title artist")
      const q = (entry.musicTitle || entry.musicArtist || '').trim();
      if (!q) {
        console.warn('[AdUpload.handleOpenMusic] no title/artist on entry', entry);
        return;
      }
    
      try {
        const res = await api.get('/music', { params: { q } });
        const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
    
        if (!list.length) {
          console.warn('[AdUpload.handleOpenMusic] no track for query', q, entry);
          return;
        }
    
        navigate(`/musicdetails/${list[0]._id}`);
      } catch (e) {
        console.error('[AdUpload.handleOpenMusic] Music lookup failed:', e);
      }
    };
    const handleContinueToOverview = () => {
      if (mediaItems.length === 0 && existingMedia.length === 0) {
        alert(t('ads.new.alerts.needMedia'));
        return;
      }
    
      const url = targetUrl.trim();
      if (!url) {
        alert(t('ads.new.alerts.needUrl'));
        return;
      }
      if (!/^https?:\/\//i.test(url)) {
        alert(
          t(
            'ads.new.alerts.badUrl',
            'Please enter a valid URL starting with http:// or https://'
          )
        );
        return;
      }
    
      const label = buttonLabel.trim();
      if (!label) {
        alert(
          t(
            'ads.new.alerts.needButtonLabel',
            'Please enter a label for your ad button (e.g. "Shop now").'
          )
        );
        return;
      }
    
      navigate('/adoverview', {
        state: {
          mediaDraft: {
            existingMedia,
            mediaItems,
          },
          targetUrl: url,       // 👈 single URL used everywhere
          buttonLabel: label,   // 👈 button text only
          fromPath: currentPath,
        },
      });
    };
    

    

// Playable source: prefer local blob URL during edit; else remote http(s)


// When building src, avoid returning "" (empty string can cause odd fetches)
const srcFor = (item: MediaItem): string | undefined => {
  const managed = objectURLs[item.id];
  if (managed) return managed;

  const u = item.url || "";
  if (!u) return undefined;

  // Never trust a blob stored on the item (can be from a different page load)
  if (u.startsWith("blob:")) return undefined;

  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${getApiBaseUrl()}${u}`;
  return undefined;
};

// Keep a ref mirror to avoid using objectURLs in the effect dependency
const objectURLsRef = React.useRef(objectURLs);
React.useEffect(() => { objectURLsRef.current = objectURLs; }, [objectURLs]);

useEffect(() => {
  const toCreate: Record<string, string> = {};
  const existing = objectURLsRef.current;

  for (const item of mediaItems) {
    if (!item.file) continue;

    const isPreviewVideo =
      item.file.type.startsWith('video/') && item.url?.startsWith('/uploadsPreview/');
    const needsObjectUrl = !item.url || isPreviewVideo;

    if (!needsObjectUrl || existing[item.id] || toCreate[item.id]) continue;

    toCreate[item.id] = URL.createObjectURL(item.file);
  }

  if (Object.keys(toCreate).length) {
    setObjectURLs(prev => ({ ...prev, ...toCreate }));
  }
}, [mediaItems]); // 👈 not depending on objectURLs anymore


// Single fetch effect
useEffect(() => {
  const fetchMediaData = async () => {
    if (!postId) return;

    try {
      const res = await api.get(`/events/${postId}`);
      const rawList: any[] = Array.isArray(res.data?.media) ? res.data.media : [];

      const fetchedMedia = rawList.map(fromServerMedia).filter(m => !!m.url);
      const dropped = rawList.length - fetchedMedia.length;
      if (dropped > 0) {
        console.warn(`[AdUpload] Dropped ${dropped} server media with invalid blob: URLs`);
      }

      setExistingMedia(fetchedMedia);
    } catch (err) {
      console.error("Failed to fetch media data:", err);
    }
  };

  fetchMediaData();
}, [postId]);


  //For reset
  useEffect(() => {
    if (shouldReset && !hasReset.current) {
      hasReset.current = true;
  
      setMediaItems([]);
      setObjectURLs({});
      console.log("🧼 Media state reset via navigation.state.reset === true");
    }
  }, [shouldReset]);
  

  const LoadingOverlay = () => (
    <div style={loadingOverlayStyle}>
      <div style={loadingContentStyle}>
      <span>{t('ad.upload.media.uploading')}</span>
        <div style={loadingDotsContainer}>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
          <span style={loadingDot}>.</span>
        </div>
      </div>
    </div>
  );

  
  return (
    <>
      {isLoading && <LoadingOverlay />}

      <div style={pageShellStyle}>
        {/* HEADER – same visual family as Home / auth pages */}
        <header style={pageHeaderStyle}>
          <div
            style={pageHeaderLeft}
            onClick={() => navigate('/advertisementstart')}
            >
            <ChoshiiLogo
              animateSheen
              fontFamily="'Playfair Display', serif"
              style={{ width: 160 }}
              padding={0}
            />
          </div>
        </header>

        {/* MAIN CONTENT */}
        <main style={pageMainStyle}>
          
          {/* Main card with sidebar + media area */}
          <div style={cardGridStyle}>
            {/* LEFT: sidebar card */}
            <aside style={sidebarContainerStyle}>
            <SidebarAdUpload
  targetUrl={targetUrl}
  onTargetUrlChange={setTargetUrl}
  // 👇 NEW
  buttonLabel={buttonLabel}
  onButtonLabelChange={setButtonLabel}
  onBack={() => navigate('/advertisementstart')}
  onContinue={handleContinueToOverview}
  isContinueDisabled={
    isLoading ||
    (mediaItems.length === 0 && existingMedia.length === 0) ||
    !targetUrl.trim() ||
    !buttonLabel.trim()    // 👈 NEW: disable when label empty
  }
/>



            </aside>

            {/* RIGHT: media composer card */}
            <section style={mediaOuterStyle}>
              <div style={postCardStyle}>
                {/* 👤 Header with Profile */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                    marginLeft: CONTENT_X_OFFSET,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '5%',
                        marginRight: 10,
                        backgroundColor: user?.picture
                          ? 'transparent'
                          : 'rgb(38, 29, 162)',
                        backgroundImage: user?.picture
                          ? `url(${user.picture})`
                          : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: user?.picture ? 0 : '30px',
                      }}
                    >
                      {!user?.picture && '👻'}
                    </div>
                    <strong>
                      {user?.profilename || t('gallery.edit.anonymous')}
                    </strong>
                  </div>
                </div>

                {/* ✅ Scrollable Media Section */}
                <div style={mediaScrollerStyle}>
                  {[...existingMedia, ...mediaItems].map((item, index) => {
                    const mediaUrl = srcFor(item);
                    const fid = getFilterId(item.id) ?? item.filterId ?? null;
                    const overlay = overlayStyleFor(fid);

                    return (
                      <div
                        key={item.id}
                        style={{
                          flex: '0 0 auto', 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'flex-start',
                          background: 'transparent',
                          position: 'relative',
                          boxSizing: 'border-box',
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        {mediaUrl ? (
                     isVideo(item) ? (
                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                        }}
                        onMouseMove={() => showMusicChipFor(item.id)}
                        onMouseLeave={() => setActiveVideoChipId(null)}
                      >
                        <video
                          src={mediaUrl}
                          controls
                          muted
                          preload="metadata"
                          style={{
                            display: 'block',
                            maxWidth: MEDIA_TILE_WIDTH,
                            maxHeight: MEDIA_TILE_WIDTH,
                            width: 'auto',
                            height: 'auto',
                            borderRadius: 12,
                          }}
                        />
                    
                        {overlay && (
                          <div
                            style={{
                              ...overlay,
                              borderRadius: 12,
                              position: 'absolute',
                              inset: 0,
                            }}
                          />
                        )}
                    
                        {!!(item.musicTitle || item.musicArtist) && (
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              right: 5,
                              bottom: -9,
                              padding: '6px 10px 9px',
                              fontSize: 12,
                              color: '#c1c9d6',
                              display: activeVideoChipId === item.id ? 'flex' : 'none',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              background:
                                'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.7) 100%)',
                              pointerEvents: 'auto',
                            }}
                            title={mediaLabel(item)}
                          >
                              <span aria-hidden="true" style={{ flex: '0 0 auto' }}>
                                🎵
                              </span>
                    
                              {(item as ExtMediaItem).musicId ? (
                                <Link
                                  to={`/musicdetails/${(item as ExtMediaItem).musicId}`}
                                  style={{
                                    minWidth: 0,
                                    color: '#c1c9d6',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  title={t('gallery.addMusic.openDetails', {
                                    title:
                                      item.musicTitle ||
                                      t('galleryPosting.trackFallback'),
                                  })}
                                >
                                  {mediaLabel(item)}
                                </Link>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenMusic({
                                      musicId: null,
                                      musicTitle: item.musicTitle ?? null,
                                      musicArtist: item.musicArtist ?? null,
                                    });
                                  }}
                                  style={{
                                    minWidth: 0,
                                    color: '#6e737a',
                                    fontWeight: 600,
                                    fontSize: 11,                               
                                    background: 'transparent',
                                    border: 0,
                                    padding: 0,
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                  title={t('gallery.addMusic.openDetails', {
                                    title:
                                      item.musicTitle ||
                                      t('galleryPosting.trackFallback'),
                                  })}
                                >
                                  {mediaLabel(item)}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                    ) : (
                      // ⬇ your image branch stays as you have it now
                      <div
  style={{
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  }}
>
  <img
    src={mediaUrl}
    alt={`media-${index}`}
    loading="lazy"
    style={{
      display: 'block',
      maxWidth: MEDIA_TILE_WIDTH,
      maxHeight: MEDIA_TILE_WIDTH,
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
      objectPosition: 'center',
      borderRadius: 12,
    }}
  />
  {overlay && (
    <div
      style={{
        ...overlay,
        borderRadius: 12,
        position: 'absolute',
        inset: 0,
      }}
    />
  )}
</div>

                    )
                    
           
                        ) : (
                          <div style={{ color: '#bbb' }}>
                            {t('ad.upload.media.noPreview')}
                          </div>
                        )}

                        {/* Action buttons */}
                        <div
                          style={{
                            width: '50%',
                            display: 'flex',
                            gap: 10,
                            justifyContent: 'center',
                            flexWrap: 'nowrap',
                            marginTop: 10,
                          }}
                        >
                          {/* Delete */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                            }}
                            style={smallActionButton}
                          >
                            {t('ad.upload.buttons.delete')}
                          </button>

                          {/* Filter (images + videos) */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();

                              addOrReplaceMediaItem({
                                id: item.id,
                                url: item.url,
                                file: item.file,
                                musicTitle: item.musicTitle ?? null,
                                musicArtist: item.musicArtist ?? null,
                                filterId: getFilterId(item.id),
                              });

                              const remoteUrl = toRemoteHttp(item.url);
                              if (isVideo(item)) {
                                navigate(`/adeditor/video/${item.id}`, {
                                  state: {
                                    sidebarData: {
                                      flow: 'ad',
                                      returnTo: '/adupload',
                                      targetUrl,
                                      buttonLabel,
                                    },
                                    editTargetId: item.id,
                                    remoteUrl,
                                    mediaItem: item,
                                  },
                                });
                              } else {
                                navigate(`/adeditor/image/${item.id}`, {
                                  state: {
                                    sidebarData: {
                                      flow: 'ad',
                                      returnTo: '/adupload',
                                      targetUrl,
                                      buttonLabel,
                                    },
                                    mediaItem: item,
                                  },
                                });
                              }
                              
                            }}
                            style={smallActionButton}
                          >
                            {t('ad.upload.buttons.filter')}
                          </button>

                          {/* Music — only for videos */}
                          {isVideo(item) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();

                                const remoteUrl = toRemoteHttp(item.url);

                                navigate('/ad-add-music', {
                                  state: {
                                    editTargetId: item.id,
                                    remoteUrl,
                                    sidebarData: {
                                      flow: 'ad',
                                      returnTo: '/adupload',
                                      targetUrl,
                                      buttonLabel,
                                    },
                                  },
                                });
                                
                                
                              }}
                              style={smallActionButton}
                            >
                               {t('ad.upload.buttons.music')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* ➕ Add Media tile */}
                  {totalMediaCount < MAX_MEDIA_PER_POST && (
                    <div
                      style={
                        totalMediaCount === 0
                          ? addMediaCardFullWidthStyle
                          : addMediaCardCompactStyle
                      }
                      onDragOver={(e) => {
                        if (!e.dataTransfer.types.includes('Files')) return;
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget === e.target) {
                          setIsDragOver(false);
                        }
                      }}
                      onDrop={handleDropFiles}
                    >
                      {isTranscoding ? (
                        <div className="spinner" />
                      ) : (
                        <label
                          style={{
                            cursor: 'pointer',
                            textAlign: 'center',
                          }}
                        >
                          <div style={{ fontSize: 32, marginBottom: 6 }}>
                            📷 🎬
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              color: '#e5e7eb',
                              marginBottom: 4,
                            }}
                          >
                            {t("ad.upload.media.addTile.title")}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: '#9ca3af',
                            }}
                          >
                            {t("ad.upload.media.addTile.subtitle")}
                          </div>

                          <input
                            type="file"
                            accept="image/*, video/*"
                            multiple
                            onChange={handleFileChange}
                            disabled={isTranscoding}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </>
  );

};


const MEDIA_LEFT_NUDGE = 0;
const MEDIA_GAP = 40;
const MEDIA_TILE_WIDTH = 610;
const CONTENT_X_OFFSET = 40;


/* 🌌 Page shell */
const pageShellStyle: React.CSSProperties = {
  minHeight: '100vh',
  height: '100vh',                         // ⬅️ lock height
  width: '100%',
  background:
    'radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)',
  color: '#f9fafb',
  fontFamily:
    'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',                      // ⬅️ no page scroll
};

/* 🧭 Header */
const pageHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0px',
  background: 'rgba(2, 6, 23, 0.1)',
  backdropFilter: 'blur(10px)',
};

const pageHeaderLeft: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  cursor: 'pointer',
};


/* 🧱 Main */
const pageMainStyle: React.CSSProperties = {
  flex: 1,
  padding: '9px 24px 32px',
};


/* Grid: sidebar + composer */
const cardGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '300px minmax(0, 1fr)',
  gap: 16,
  alignItems: 'flex-start',
};

/* Sidebar container */
const sidebarContainerStyle: React.CSSProperties = {
  position: 'sticky',
  top: 80,
  alignSelf: 'flex-start',
  maxHeight: 'calc(100vh - 100px)',
  overflowY: 'auto',
};

/* Composer area outer */
const mediaOuterStyle: React.CSSProperties = {
  width: '100%',
};    

/* Composer card */
const postCardStyle: React.CSSProperties = {
  background:
    'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(4, 7, 15, 0.95))',
  padding: '20px 18px 40px',
  borderRadius: 24,
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  border: '1px solid rgba(148,163,184,0.35)',
  color: '#e5e7eb',
  height: 'calc(91vh - 88px)',

};

/* Horizontal media scroller */
const mediaScrollerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: `${MEDIA_GAP}px`,
  paddingBottom: 10,
  paddingLeft: CONTENT_X_OFFSET,    // ⬅️ aligns media with header
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  whiteSpace: 'nowrap',
  transform: `translateX(${MEDIA_LEFT_NUDGE}px)`, // now 0
  alignItems: 'center', 
};


/* Small action buttons under each media */
const smallActionButton: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: '#020617',
  border: '1px solid #cbd5f5',
  borderRadius: 999,
  color: '#cbd5f5',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
};


const loadingOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const loadingContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontSize: '24px',
  color: '#380675',
};

const loadingDotsContainer: React.CSSProperties = {
  display: 'flex',
  gap: '5px',

};

const loadingDot: React.CSSProperties = {
  animation: 'loadingDots 1s infinite',
  fontSize: '36px',
};

const musicRowStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const musicTextStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};


/* ➕ Add media tile – base */
const addMediaBaseStyle: React.CSSProperties = {
  justifyContent: 'center',
  display: 'flex',
  alignItems: 'center',
  border: '2px dashed rgba(148,163,184,0.6)',
  borderRadius: 20,
  background:
    'radial-gradient(circle at top, #020617, #020617 40%, #000 100%)',
};

/* Empty state → fills the container more */
const addMediaCardFullWidthStyle: React.CSSProperties = {
  ...addMediaBaseStyle,
  flex: '1 1 auto',
  maxWidth: 'min(1000px, 100%)',
  height: 440,
  maxHeight: 480,
  marginLeft: 0,
  marginTop: 40,
};

/* Non-empty state → compact tile to the right */
const addMediaCardCompactStyle: React.CSSProperties = {
  ...addMediaBaseStyle,
  flex: '0 0 auto',
  width: 420,
  height: 420,
  maxHeight: 500,
  marginTop: -40,

};


export default AdUpload;
